import styles from 'css/src/components/common/ui/BadgeButton.module.scss';

import React from 'react';
import Immutable from 'immutable';
import classNames from 'classnames';

import { CanvasPreview } from 'js/src/components/common/ui/CanvasPreview';
import { Dropdown, type DropdownItemProp } from 'js/src/components/common/ui/Dropdown';
import type { ImmutableMap } from 'js/src/libs/consts';

export interface BadgeButtonProps {
    icon: string | React.ReactNode;
    label: React.ReactNode;
    closeable?: boolean;
    disabled?: boolean;
    onClick?: VoidFunction;
    onClose?: VoidFunction;
    thumbnail?: HTMLCanvasElement;
    className?: string;
}

const BadgeButton = React.memo(function BadgeButton({
    icon,
    label,
    closeable = false,
    disabled = false,
    onClick,
    onClose,
    thumbnail,
    className,
}: BadgeButtonProps) {
    const [isHovered, setIsHovered] = React.useState(false);
    const labelTitle = typeof label === 'string' ? label : undefined;

    const closeIcon = (
        <i
            translate="no"
            className={classNames('material-symbols-outlined', styles['l5-badge-button__icon'])}
        >
            close
        </i>
    );
    const defaultIcon =
        typeof icon === 'string' ? (
            <i
                translate="no"
                className={classNames('material-symbols-outlined', styles['l5-badge-button__icon'])}
            >
                {icon}
            </i>
        ) : (
            <span className={styles['l5-badge-button__icon']}>{icon}</span>
        );

    if (!closeable) {
        return (
            <div
                className={classNames(
                    'd-flex align-items-center gap-2 px-l5-12 rounded-pill',
                    styles['l5-badge-button'],
                    styles['l5-badge-button--clickable'],
                    className,
                    { [styles['l5-badge-button--disabled']]: disabled },
                )}
                onClick={disabled ? undefined : onClick}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onKeyDown={(e) => {
                    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onClick?.();
                    }
                }}
            >
                {defaultIcon}
                <span
                    className={classNames('text-nowrap', styles['l5-badge-button__label'])}
                    title={labelTitle}
                >
                    {label}
                </span>
            </div>
        );
    }

    const iconContent = (() => {
        if (!thumbnail) {
            return isHovered && !disabled ? closeIcon : defaultIcon;
        }
        return (
            <div className={styles['l5-badge-button__icon-container']}>
                {isHovered && !disabled ? (
                    closeIcon
                ) : (
                    <CanvasPreview
                        sourceCanvas={thumbnail}
                        className={styles['l5-badge-button__thumbnail']}
                    />
                )}
            </div>
        );
    })();

    return (
        <div
            className={classNames(
                'd-flex rounded-pill position-relative',
                styles['l5-badge-button'],
                className,
                { [styles['l5-badge-button--disabled']]: disabled },
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <div
                className={classNames(
                    'd-flex align-items-center',
                    styles['l5-badge-button__close-zone'],
                )}
                onClick={disabled ? undefined : onClose}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onKeyDown={(e) => {
                    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onClose?.();
                    }
                }}
            >
                {iconContent}
            </div>
            <div
                className={classNames(
                    'd-flex align-items-center',
                    styles['l5-badge-button__edit-zone'],
                )}
                onClick={disabled ? undefined : onClick}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onKeyDown={(e) => {
                    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onClick?.();
                    }
                }}
            >
                <span
                    className={classNames('text-nowrap', styles['l5-badge-button__label'])}
                    title={labelTitle}
                >
                    {label}
                </span>
            </div>
        </div>
    );
});

export default BadgeButton;

const DropdownBadgeToggle = React.memo(function DropdownBadgeToggle({
    icon,
    label,
    closeable,
    disabled,
    onClose,
    thumbnail,
    className,
    onClick,
    ref,
}: {
    icon: string | React.ReactNode;
    label: React.ReactNode;
    closeable: boolean;
    disabled?: boolean;
    onClose?: VoidFunction;
    thumbnail?: HTMLCanvasElement;
    className?: string;
    onClick: (e: React.MouseEvent) => void;
    ref?: React.Ref<HTMLDivElement>;
}) {
    const [isHovered, setIsHovered] = React.useState(false);
    const labelTitle = typeof label === 'string' ? label : undefined;

    const closeIcon = (
        <i
            translate="no"
            className={classNames('material-symbols-outlined', styles['l5-badge-button__icon'])}
        >
            close
        </i>
    );
    const defaultIcon =
        typeof icon === 'string' ? (
            <i
                translate="no"
                className={classNames('material-symbols-outlined', styles['l5-badge-button__icon'])}
            >
                {icon}
            </i>
        ) : (
            <span className={styles['l5-badge-button__icon']}>{icon}</span>
        );

    if (!closeable) {
        return (
            <button
                type="button"
                ref={ref as React.Ref<HTMLButtonElement>}
                className={classNames(
                    'd-flex align-items-center gap-2 px-l5-12 rounded-pill',
                    styles['l5-badge-button'],
                    styles['l5-badge-button--clickable'],
                    className,
                    { [styles['l5-badge-button--disabled']]: disabled },
                )}
                onClick={disabled ? undefined : onClick}
                disabled={disabled}
            >
                {defaultIcon}
                <span
                    className={classNames('text-nowrap', styles['l5-badge-button__label'])}
                    title={labelTitle}
                >
                    {label}
                </span>
            </button>
        );
    }

    const iconContent = (() => {
        if (!thumbnail) {
            return isHovered && !disabled ? closeIcon : defaultIcon;
        }
        return (
            <div className={styles['l5-badge-button__icon-container']}>
                {isHovered && !disabled ? (
                    closeIcon
                ) : (
                    <CanvasPreview
                        sourceCanvas={thumbnail}
                        className={styles['l5-badge-button__thumbnail']}
                    />
                )}
            </div>
        );
    })();

    return (
        <div
            ref={ref}
            className={classNames(
                'd-flex rounded-pill position-relative',
                styles['l5-badge-button'],
                className,
                { [styles['l5-badge-button--disabled']]: disabled },
            )}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onClick={disabled ? undefined : onClick}
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
        >
            <div
                className={classNames(
                    'd-flex align-items-center',
                    styles['l5-badge-button__close-zone'],
                )}
                onClick={(e) => {
                    if (disabled) return;
                    e.stopPropagation();
                    onClose?.();
                }}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                onKeyDown={(e) => {
                    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                        e.preventDefault();
                        onClose?.();
                    }
                }}
            >
                {iconContent}
            </div>
            <div
                className={classNames(
                    'd-flex align-items-center',
                    styles['l5-badge-button__edit-zone'],
                )}
            >
                <span
                    className={classNames('text-nowrap', styles['l5-badge-button__label'])}
                    title={labelTitle}
                >
                    {label}
                </span>
            </div>
        </div>
    );
});

export interface DropdownBadgeButtonProps<T extends object> {
    icon: string | React.ReactNode;
    label: React.ReactNode;
    closeable?: boolean;
    disabled?: boolean;
    onClose?: VoidFunction;
    thumbnail?: HTMLCanvasElement;
    className?: string;
    itemPropsList: Immutable.List<DropdownItemProp<T>>;
    onSelect: (data: ImmutableMap<T>, e: React.MouseEvent) => void;
    breakOverflow?: boolean;
    limitMenuHeight?: boolean;
}

export function DropdownBadgeButton<T extends object>({
    icon,
    label,
    closeable = false,
    disabled = false,
    onClose,
    thumbnail,
    className,
    itemPropsList,
    onSelect,
    breakOverflow,
    limitMenuHeight,
}: DropdownBadgeButtonProps<T>) {
    const toggleProps = React.useMemo(
        () =>
            Immutable.Map({
                icon,
                label,
                closeable,
                disabled,
                onClose,
                thumbnail,
                className,
            }) as Immutable.Map<string, unknown>,
        [icon, label, closeable, disabled, onClose, thumbnail, className],
    );

    return (
        <Dropdown
            drop="down"
            ToggleComponent={DropdownBadgeToggle}
            toggleProps={toggleProps}
            itemPropsList={itemPropsList}
            onSelect={onSelect}
            breakOverflow={breakOverflow}
            limitMenuHeight={limitMenuHeight}
        />
    );
}
